import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Package,
  Truck,
  XCircle,
  MapPin,
  User,
  CreditCard,
  Printer,
  Check,
  Storefront,
  ShieldCheck,
  Phone,
  Receipt,
  CheckCircle,
  Clock,
  Star,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import {
  Order,
  FulfillmentStatus,
  MERCHANT_ROUTES,
  apiClient,
  useAuthStore,
} from '@dukene/shared';

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const [isEditingTracking, setIsEditingTracking] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');

  const { data: order, isLoading, error } = useQuery<Order>({
    queryKey: ['order', activeStoreId, id],
    queryFn: async () => {
      return await apiClient.get<Order>(`/orders/${id}`);
    },
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
    enabled: !!id && !!activeStoreId,
  });

  const updateFulfillmentStatusMutation = useMutation({
    mutationFn: async (data: { fulfillmentStatus: FulfillmentStatus; trackingNumber?: string }) => {
      return await apiClient.patch(`/orders/${id}/fulfillment`, data);
    },
    onSuccess: () => {
      const storeId = useAuthStore.getState().activeStoreId;
      queryClient.invalidateQueries({ queryKey: ['order', storeId, id] });
      queryClient.invalidateQueries({ queryKey: ['merchant-orders', storeId] });
      setIsEditingTracking(false);
    },
  });

  const updateTrackingMutation = useMutation({
    mutationFn: async (tracking: string) => {
      return await apiClient.patch(`/orders/${id}/fulfillment`, {
        fulfillmentStatus: order?.fulfillmentStatus || 'UNFULFILLED',
        trackingNumber: tracking,
      });
    },
    onSuccess: () => {
      const storeId = useAuthStore.getState().activeStoreId;
      queryClient.invalidateQueries({ queryKey: ['order', storeId, id] });
      setIsEditingTracking(false);
      setTrackingNumber('');
    },
  });

  const updateOrderStatusMutation = useMutation({
    mutationFn: async (status: 'SHIPPED' | 'DELIVERED') => {
      return await apiClient.patch(`/orders/${id}/status`, { status });
    },
    onSuccess: () => {
      const storeId = useAuthStore.getState().activeStoreId;
      queryClient.invalidateQueries({ queryKey: ['order', storeId, id] });
      queryClient.invalidateQueries({ queryKey: ['merchant-orders', storeId] });
    },
  });

  if (isLoading) {
    return (
      <div className="p-12 space-y-4 animate-pulse">
        <div className="h-10 bg-[#F6F6F7] dark:bg-slate-900 rounded-xl" />
        <div className="h-64 bg-[#F6F6F7] dark:bg-slate-900 rounded-xl" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl p-6 text-center">
        <XCircle weight="fill" className="w-10 h-10 text-red-600 mx-auto mb-2" />
        <h2 className="text-sm font-bold text-red-900 dark:text-red-200">
          Order Details Unavailable
        </h2>
        <p className="text-xs text-red-700 dark:text-red-300 mt-1">
          The requested order could not be loaded.
        </p>
        <button
          type="button"
          onClick={() => navigate(MERCHANT_ROUTES.ORDERS)}
          className="mt-4 h-8 px-4 bg-[#006838] text-white text-xs font-semibold rounded-lg"
        >
          Return to Orders
        </button>
      </div>
    );
  }

  const first = order.customer?.firstName || '';
  const last = order.customer?.lastName || '';
  const fullName = `${first} ${last}`.trim();
  const customerFullName =
    fullName ||
    order.customer?.name ||
    (order.source === 'POS' ? 'Walk in' : order.email || 'Guest Customer');

  const salesChannel = (order as any).salesChannel || 
    ((order as any).transactions?.[0]?.metadata?.salesChannel) ||
    ((order as any).transactions?.[0]?.metadata?.source === 'pos' || (order as any).email?.includes('pos-walkin') ? 'POS Terminal' : 'Online Storefront');

  const paymentDetails = (order as any).paymentDetails || {
    provider: (order as any).paymentMethod || (order as any).transactions?.[0]?.paymentMethod || 'M-Pesa Checkout',
    confirmationCode: (order as any).paymentTransactions?.[0]?.providerRef || (order as any).paymentTransactions?.[0]?.dukeneRef || (order as any).transactions?.[0]?.transactionId || null,
    phone: (order as any).paymentTransactions?.[0]?.phone || (order as any).phone || order.customer?.phone || null,
    amount: Number((order as any).paymentTransactions?.[0]?.amount || order.total || 0),
    status: (order as any).paymentStatus || (order as any).transactions?.[0]?.status || 'PENDING',
    confirmedAt: (order as any).paymentTransactions?.[0]?.confirmedAt || order.createdAt || null
  };
  const reviews = ((order as any).reviews || []) as Array<{
    id: string;
    rating: number;
    title?: string | null;
    comment?: string | null;
    status: string;
    isAnonymous?: boolean;
    createdAt: string;
    product?: { title?: string };
    customer?: { firstName?: string; lastName?: string; email?: string };
  }>;

  const isPaymentCompleted = ['PAID', 'COMPLETED', 'CONFIRMED', 'SUCCESS'].includes(
    String(paymentDetails.status).toUpperCase()
  );

  return (
    <div className="space-y-5">
      {/* Back Link & Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(MERCHANT_ROUTES.ORDERS)}
            className="p-1.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] transition-colors"
            title="Back to orders list"
          >
            <ArrowLeft weight="bold" className="w-4 h-4" />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-base font-bold text-[#202223] dark:text-white font-mono">
                Order #{order.orderNumber || order.id.slice(0, 8)}
              </h1>
              <span className="bg-[#E3F1DF] text-[#006838] border border-[#B3DEC1] px-2 py-0.5 rounded text-[10px] font-bold">
                {order.status}
              </span>
              <span className="bg-[#F0F2F4] dark:bg-slate-800 text-[#202223] dark:text-slate-200 border border-[#D2D5D9] dark:border-slate-700 px-2 py-0.5 rounded text-[10px] font-semibold flex items-center gap-1">
                <Storefront weight="fill" className="w-3 h-3 text-[#006838]" />
                {salesChannel}
              </span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1 border ${
                ['PAID', 'COMPLETED', 'CONFIRMED', 'SUCCESS'].includes(
                  String(paymentDetails.status).toUpperCase()
                )
                  ? 'bg-[#E3F1DF] text-[#006838] border-[#B3DEC1]'
                  : paymentDetails.status === 'FAILED'
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
                {['PAID', 'COMPLETED', 'CONFIRMED', 'SUCCESS'].includes(
                  String(paymentDetails.status).toUpperCase()
                ) ? (
                  <CheckCircle weight="fill" className="w-3 h-3" />
                ) : (
                  <Clock weight="fill" className="w-3 h-3" />
                )}
                Payment {paymentDetails.status}
              </span>
            </div>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
              Placed on{' '}
              {order.createdAt
                ? format(new Date(order.createdAt), 'MMMM d, yyyy HH:mm')
                : 'Unknown date'}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => window.print()}
          className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-[#F6F6F7] text-xs font-semibold text-[#202223] dark:text-white inline-flex items-center gap-1.5 shrink-0"
        >
          <Printer weight="bold" className="w-3.5 h-3.5 text-[#5C5F62]" />
          Print Order
        </button>
      </div>

      {/* Main 2-Column Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left Columns (Line Items & Financial Summary) */}
        <div className="lg:col-span-2 space-y-5">
          {/* Order Items Table */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/40">
              <h2 className="text-sm font-semibold text-[#202223] dark:text-white">
                Ordered Items
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/30 dark:bg-slate-900/20 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
                    <th className="py-2.5 px-4">Product / Item</th>
                    <th className="py-2.5 px-4 text-right">Price</th>
                    <th className="py-2.5 px-4 text-right">Qty</th>
                    <th className="py-2.5 px-4 text-right">Line Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800">
                  {order.items && order.items.length > 0 ? (
                    order.items.map((item: any, idx: number) => (
                      <tr key={item.id || idx}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-[#F6F6F7] dark:bg-slate-800 flex items-center justify-center text-[#006838] font-bold text-xs shrink-0">
                              <Package weight="fill" className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="font-semibold text-[#202223] dark:text-white">
                                {item.productName || item.name || 'Product Item'}
                              </p>
                              {item.sku && (
                                <p className="text-[10px] text-[#5C5F62] font-mono mt-0.5">
                                  SKU: {item.sku}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="py-3 px-4 text-right font-mono text-[#5C5F62]">
                          KES {Number(item.price || 0).toLocaleString()}
                        </td>

                        <td className="py-3 px-4 text-right font-mono font-bold text-[#202223] dark:text-white">
                          × {item.quantity || 1}
                        </td>

                        <td className="py-3 px-4 text-right font-mono font-bold text-[#006838]">
                          KES {Number((item.price || 0) * (item.quantity || 1)).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-xs text-[#5C5F62]">
                        No individual line items stored for this order record.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Financial Summary */}
            <div className="p-4 border-t border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/30 dark:bg-slate-900/20 space-y-2 text-xs">
              <div className="flex justify-between text-[#5C5F62] dark:text-slate-400">
                <span>Subtotal</span>
                <span className="font-mono">KES {Number(order.total || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-[#5C5F62] dark:text-slate-400">
                <span>Shipping Fee</span>
                <span className="font-mono">KES 0</span>
              </div>
              <div className="flex justify-between font-bold text-sm text-[#202223] dark:text-white pt-2 border-t border-[#E1E4E8] dark:border-slate-800">
                <span>Total Amount</span>
                <span className="font-mono text-[#006838]">
                  KES {Number(order.total || 0).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/40">
              <h2 className="text-sm font-semibold text-[#202223] dark:text-white">
                Customer Reviews for This Order
              </h2>
            </div>
            <div className="p-4 space-y-4">
              {reviews.length > 0 ? reviews.map((review) => {
                const reviewerName = review.isAnonymous
                  ? 'Anonymous customer'
                  : `${review.customer?.firstName || ''} ${review.customer?.lastName || ''}`.trim() ||
                    review.customer?.email ||
                    'Customer';
                return (
                  <div key={review.id} className="rounded-lg border border-[#E1E4E8] dark:border-slate-800 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-semibold text-[#202223] dark:text-white">
                          {review.product?.title || 'Product'}
                        </p>
                        <p className="text-[11px] text-[#5C5F62] mt-0.5">
                          {reviewerName} · {format(new Date(review.createdAt), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <span className="text-[10px] font-semibold uppercase text-[#5C5F62]">
                        {review.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5 mt-2" aria-label={`${review.rating} out of 5 stars`}>
                      {[1, 2, 3, 4, 5].map((value) => (
                        <Star
                          key={value}
                          weight={value <= review.rating ? 'fill' : 'regular'}
                          className={value <= review.rating ? 'w-4 h-4 text-amber-500' : 'w-4 h-4 text-slate-300'}
                        />
                      ))}
                    </div>
                    {review.title && <p className="text-xs font-semibold mt-2">{review.title}</p>}
                    {review.comment && (
                      <p className="text-xs text-[#5C5F62] dark:text-slate-300 mt-1 whitespace-pre-wrap">
                        {review.comment}
                      </p>
                    )}
                  </div>
                );
              }) : (
                <p className="text-xs text-[#5C5F62]">
                  No reviews have been submitted for this order yet.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Fulfillment & Customer Info */}
        <div className="space-y-5">
          {/* Fulfillment Action Card */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-3.5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#202223] dark:text-white">
                Fulfillment Status
              </h3>
              <Truck weight="fill" className="w-4 h-4 text-[#006838]" />
            </div>

            <div className="p-3 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 flex items-center justify-between text-xs">
              <span className="text-[#5C5F62] dark:text-slate-400">Status</span>
              <span className="font-bold uppercase text-[#006838]">
                {order.fulfillmentStatus || 'UNFULFILLED'}
              </span>
            </div>

            <div className="space-y-2">
              <button
                type="button"
                disabled={
                  order.fulfillmentStatus === FulfillmentStatus.FULFILLED ||
                  ['SHIPPED', 'DELIVERED', 'CANCELLED', 'REFUNDED'].includes(order.status)
                }
                onClick={() =>
                  updateFulfillmentStatusMutation.mutate({
                    fulfillmentStatus: FulfillmentStatus.FULFILLED,
                  })
                }
                className="w-full h-8 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
              >
                <Check weight="bold" className="w-3.5 h-3.5" />
                {order.fulfillmentStatus === FulfillmentStatus.FULFILLED
                  ? 'Fulfilled / Prepared'
                  : 'Mark as Fulfilled / Prepared'}
              </button>

              <button
                type="button"
                disabled={
                  !isPaymentCompleted ||
                  order.fulfillmentStatus !== FulfillmentStatus.FULFILLED ||
                  ['SHIPPED', 'DELIVERED', 'CANCELLED', 'REFUNDED'].includes(order.status) ||
                  updateOrderStatusMutation.isPending
                }
                title={!isPaymentCompleted ? "Payment must be completed before shipping" : ""}
                onClick={() => updateOrderStatusMutation.mutate('SHIPPED')}
                className="w-full h-8 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
              >
                <Truck weight="bold" className="w-3.5 h-3.5" />
                {['SHIPPED', 'DELIVERED'].includes(order.status)
                  ? 'Marked as Shipped'
                  : 'Mark as Shipped'}
              </button>

              <button
                type="button"
                disabled={
                  order.status !== 'SHIPPED' ||
                  updateOrderStatusMutation.isPending
                }
                onClick={() => updateOrderStatusMutation.mutate('DELIVERED')}
                className="w-full h-8 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
              >
                <CheckCircle weight="bold" className="w-3.5 h-3.5" />
                {order.status === 'DELIVERED' ? 'Marked as Delivered' : 'Mark as Delivered'}
              </button>

              {(updateFulfillmentStatusMutation.isError || updateOrderStatusMutation.isError) && (
                <p className="text-[11px] text-red-600">
                  The order status could not be updated. Please try again.
                </p>
              )}

              {isEditingTracking ? (
                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Enter Tracking ID..."
                    className="flex-1 h-8 px-2 rounded border border-[#D2D5D9] bg-white text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => updateTrackingMutation.mutate(trackingNumber)}
                    className="h-8 px-3 bg-[#006838] text-white rounded text-xs font-semibold"
                  >
                    Save
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsEditingTracking(true)}
                  className="w-full h-8 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-[#F6F6F7] text-[#202223] dark:text-white text-xs font-semibold transition-colors"
                >
                  Add Courier Tracking ID
                </button>
              )}
            </div>
          </div>

          {/* Sales Channel & Payment Confirmation Card */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-3.5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#202223] dark:text-white flex items-center gap-2">
                <Storefront weight="fill" className="w-4 h-4 text-[#006838]" />
                Sales Channel & Payment
              </h3>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                ['PAID', 'COMPLETED', 'CONFIRMED', 'SUCCESS'].includes(
                  String(paymentDetails.status).toUpperCase()
                )
                  ? 'bg-[#E3F1DF] text-[#006838] border border-[#B3DEC1]'
                  : paymentDetails.status === 'FAILED'
                  ? 'bg-red-100 text-red-800 border border-red-200'
                  : 'bg-amber-100 text-amber-800 border border-amber-200'
              }`}>
                {paymentDetails.status || 'PENDING'}
              </span>
            </div>

            <div className="p-3 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-[#5C5F62] dark:text-slate-400">Sales Channel</span>
                <span className="font-semibold text-[#202223] dark:text-white flex items-center gap-1.5">
                  <Storefront weight="bold" className="w-3.5 h-3.5 text-[#006838]" />
                  {salesChannel}
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-[#E1E4E8] dark:border-slate-800">
                <span className="text-[#5C5F62] dark:text-slate-400">Payment Method</span>
                <span className="font-semibold text-[#202223] dark:text-white">
                  {paymentDetails.provider}
                </span>
              </div>
            </div>

            <div className="space-y-2.5 text-xs">
              <div className="flex items-start gap-2.5">
                <ShieldCheck weight="bold" className="w-4 h-4 text-[#006838] shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[#202223] dark:text-white">Confirmation / Receipt</p>
                  <p className="text-[#006838] font-mono font-bold mt-0.5">
                    {paymentDetails.confirmationCode ||
                      (['PAID', 'COMPLETED', 'CONFIRMED', 'SUCCESS'].includes(
                        String(paymentDetails.status).toUpperCase()
                      )
                        ? 'CONFIRMED (POS/CASH)'
                        : 'Pending Confirmation')}
                  </p>
                </div>
              </div>

              {paymentDetails.phone && (
                <div className="flex items-start gap-2.5 pt-2.5 border-t border-[#E1E4E8] dark:border-slate-800">
                  <Phone weight="bold" className="w-4 h-4 text-[#8C9196] shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#202223] dark:text-white">Payer Phone</p>
                    <p className="text-[#5C5F62] dark:text-slate-400 font-mono mt-0.5">
                      {paymentDetails.phone}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2.5 pt-2.5 border-t border-[#E1E4E8] dark:border-slate-800">
                <Receipt weight="bold" className="w-4 h-4 text-[#8C9196] shrink-0 mt-0.5" />
                <div className="flex-1 flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-[#202223] dark:text-white">Amount Confirmed</p>
                    {paymentDetails.confirmedAt && (
                      <p className="text-[10px] text-[#5C5F62] dark:text-slate-400 mt-0.5">
                        {format(new Date(paymentDetails.confirmedAt), 'MMM d, yyyy HH:mm')}
                      </p>
                    )}
                  </div>
                  <span className="font-mono font-bold text-sm text-[#006838]">
                    KES {Number(paymentDetails.amount).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Profile Card */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-3.5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#202223] dark:text-white">
                Customer Details
              </h3>
              <User weight="fill" className="w-4 h-4 text-[#5C5F62]" />
            </div>

            <div className="space-y-2.5 text-xs">
              <div className="flex items-start gap-2.5">
                <User weight="bold" className="w-4 h-4 text-[#8C9196] shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[#202223] dark:text-white">{customerFullName}</p>
                  {order.customer?.email && (
                    <p className="text-[#5C5F62] dark:text-slate-400 mt-0.5">
                      {order.customer.email}
                    </p>
                  )}
                  {order.customer?.phone && (
                    <p className="text-[#5C5F62] dark:text-slate-400 font-mono mt-0.5">
                      {order.customer.phone}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2.5 pt-2.5 border-t border-[#E1E4E8] dark:border-slate-800">
                <MapPin weight="bold" className="w-4 h-4 text-[#8C9196] shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[#202223] dark:text-white">Delivery Address</p>
                  <p className="text-[#5C5F62] dark:text-slate-400 mt-0.5 leading-relaxed">
                    {(order as any).shippingAddress?.address ||
                      (order as any).shippingAddress?.street ||
                      'No street address specified'}
                    <br />
                    {(order as any).shippingAddress?.city || 'Nairobi'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2.5 pt-2.5 border-t border-[#E1E4E8] dark:border-slate-800">
                <Storefront weight="bold" className="w-4 h-4 text-[#8C9196] shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[#202223] dark:text-white">Sales Channel</p>
                  <p className="text-[#5C5F62] dark:text-slate-400 mt-0.5 font-medium text-[#006838]">
                    {salesChannel}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2.5 pt-2.5 border-t border-[#E1E4E8] dark:border-slate-800">
                <CreditCard weight="bold" className="w-4 h-4 text-[#8C9196] shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[#202223] dark:text-white">Payment Gateway</p>
                  <p className="text-[#5C5F62] dark:text-slate-400 mt-0.5">
                    {paymentDetails.provider || (order as any).paymentMethod || 'M-Pesa Checkout'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
